import { useEffect, useState } from 'react';

type Props = {
  /** Seconds remaining, when the section asks for a countdown. */
  seconds: number | null;
};

/**
 * How long a live sale has left.
 *
 * Seeded from a server-computed number of seconds rather than from an end timestamp,
 * because the customer's clock is not ours to trust — a machine an hour fast would be
 * told a live sale had ended.
 *
 * Only ever drawn for a sale that is running. A sale that has ended is not a page with a
 * notice on it: the page carries the artwork and `SaleEndedDialog`, and nothing else.
 */
export default function SaleCountdown({ seconds }: Props) {
  if (!seconds || seconds <= 1) {
    return null;
  }

  return <Countdown seconds={seconds} />;
}

function Countdown({ seconds }: { seconds: number }) {
  const [remaining, setRemaining] = useState(seconds);

  useEffect(() => {
    setRemaining(seconds);

    const timer = setInterval(() => setRemaining((value) => (value <= 1 ? 0 : value - 1)), 1000);

    return () => clearInterval(timer);
  }, [seconds]);

  if (remaining <= 0) {
    return null;
  }

  const days = Math.floor(remaining / 86400);
  const hours = Math.floor((remaining % 86400) / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const secs = remaining % 60;

  const parts = [
    days > 0 ? `${days} ${days === 1 ? 'day' : 'days'}` : null,
    `${hours} ${hours === 1 ? 'hour' : 'hours'}`,
    `${minutes} ${minutes === 1 ? 'min' : 'mins'}`,
    `${secs} ${secs === 1 ? 'sec' : 'secs'}`,
  ].filter(Boolean);

  return (
    <div className="bg-slate-900 text-white text-center py-2 px-4 text-sm font-semibold">
      {/*
        Polite and not assertive: it changes every second, and an assertive region would
        interrupt a screen reader every one of them.
      */}
      <span aria-live="off">This deal expires in {parts.join(' ')}</span>
    </div>
  );
}
